import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';

const ACTIVE = ['queued', 'generating'];

const GenerationNotifier = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [dismissedId, setDismissedId] = useState(null);
  const notified = useRef(new Set());

  const poll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/practice-tests/jobs`);
      const running = data.find((j) => ACTIVE.includes(j.status));
      setActive(running || null);

      const ready = data.find((j) => j.status === 'ready' && !j.notified && !notified.current.has(j.id));
      if (ready) {
        notified.current.add(ready.id);
        toast.success('Your practice test is ready!', {
          description: `${ready.exam} • ${ready.question_count || ''} questions`,
          duration: 12000,
          action: { label: 'Start now', onClick: () => navigate(`/practice/${ready.test_id}`) },
        });
        axios.post(`${API}/practice-tests/jobs/${ready.id}/ack`).catch(() => {});
      }

      const failed = data.find((j) => j.status === 'failed' && !j.notified && !notified.current.has(j.id));
      if (failed) {
        notified.current.add(failed.id);
        toast.error('Practice test generation failed', {
          description: failed.message || 'Please try generating again.',
          duration: 10000,
          action: { label: 'Retry', onClick: () => navigate('/practice') },
        });
        axios.post(`${API}/practice-tests/jobs/${failed.id}/ack`).catch(() => {});
      }
    } catch (error) {
      console.warn('Generation job poll failed:', error?.message);
    }
  }, [navigate]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [poll]);

  if (!active || dismissedId === active.id) return null;

  return (
    <div
      className="bg-violet-600 text-white"
      data-testid="generation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3 text-sm">
        <Loader2 className="w-4 h-4 animate-spin shrink-0" />
        <Sparkles className="w-4 h-4 shrink-0 hidden sm:block" />
        <span className="flex-1 truncate" data-testid="generation-banner-message">
          Generating your {active.exam} practice test — {active.progress}% · {active.message}
        </span>
        <Button
          size="sm"
          variant="secondary"
          className="rounded-full h-7 text-violet-700"
          onClick={() => navigate(`/practice/generating/${active.id}`)}
          data-testid="generation-banner-view-button"
        >
          View progress
        </Button>
        <button
          type="button"
          onClick={() => setDismissedId(active.id)}
          className="p-1 hover:bg-white/20 rounded-full"
          data-testid="generation-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default GenerationNotifier;
